import React, { useEffect, useState } from "react";
import { Card, Button, Table, Form, Alert, Pagination, InputGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import { IconSearch, IconReload } from "@tabler/icons-react";
import StatusFilterBar from "../components/StatusFilterBar";
import { Helmet } from "react-helmet-async";

const ManualIndexTasks = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urls, setUrls] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("creation_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [urlsPerPage, setUrlsPerPage] = useState(50);
  const [summary, setSummary] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [user, setUser] = useState(null);
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [singleCheckLoadingId, setSingleCheckLoadingId] = useState(null);
  const [finalCheckLoading, setFinalCheckLoading] = useState(false);


  const isAdmin = user?.role === "admin";

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = `/api/admin/manual-index/urls?limit=${urlsPerPage}&skip=${
        (currentPage - 1) * urlsPerPage
      }&ordering=${sortBy}&direction=${sortOrder}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      if (activeFilter !== null) {
        url += `&status=${encodeURIComponent(activeFilter)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Ошибка загрузки данных");

      const data = await response.json();

      setUrls(data.items || []);

      // Маппинг summary под StatusFilterBar (как на AllTasks)
      const raw = data.summary || {};
      setSummary({
        all: raw.total ?? 0,
        indexed: raw.indexed ?? 0,
        in_progress: raw.in_progress ?? 0,
        non_indexed: raw.non_indexed ?? 0,
        non_indexables: raw.non_indexables ?? 0,
      });

      setTotalCount(data.total_count || 0);
      setTotalPages(Math.max(1, Math.ceil((data.total_count || 0) / urlsPerPage)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/auth/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUser(data);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    fetchUrls();
  }, [currentPage, sortBy, sortOrder, urlsPerPage, activeFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter]);

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set([1, total]);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const renderSortHeader = (label, field) => (
    <th
      style={{ cursor: "pointer" }}
      onClick={() => {
        if (sortBy === field) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(field);
          setSortOrder("asc");
        }
      }}
    >
      {label} {sortBy === field && (sortOrder === "asc" ? "▲" : "▼")}
    </th>
  );

  const runManualCheckAll = async () => {
    if (!window.confirm("Запустить разовую проверку для всех Request in progress?")) return;

    try {
      setRunAllLoading(true);

      const nowIso = new Date().toISOString();
      setUrls((prev) =>
        prev.map((u) =>
          u.index_google_status === "Request in progress"
            ? { ...u, manual_check_status: "In progress", manual_check_date: nowIso }
            : u
        )
      );

      const token = localStorage.getItem("token");
      const res = await fetch("/api/admin/manual-index/run-all", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let msg = "Ошибка запуска проверки";
        try {
          const errJson = await res.json();
          if (errJson?.detail) msg = errJson.detail;
        } catch {}
        throw new Error(msg);
      }
    } catch (err) {
      alert("Не удалось запустить разовую проверку: " + err.message);
    } finally {
      setRunAllLoading(false);
    }
  };

const runFinalCheckSelected = async () => {
  if (selectedUrls.length === 0) {
    alert("Please select at least one URL for final index check.");
    return;
  }

  if (
    !window.confirm(
      "Run final index check with refund for the selected URLs?"
    )
  ) {
    return;
  }

  try {
    setFinalCheckLoading(true);

    const token = localStorage.getItem("token");
    const res = await fetch("/api/admin/manual-index/final-check", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ url_ids: selectedUrls }),
    });

    if (!res.ok) {
      let msg = "Failed to run final index check.";
      try {
        const errJson = await res.json();
        if (errJson?.detail) msg = errJson.detail;
      } catch (e) {
        // ignore json parse errors
      }
      alert(msg);
      return;
    }

    // Успех: локально обновляем статусы для выбранных URL,
    // у которых статус был Request in progress
    setUrls((prev) =>
      prev.map((u) =>
        selectedUrls.includes(u.id) &&
        u.index_google_status === "Request in progress"
          ? {
              ...u,
              index_google_status: "Non indexed (refunded)",
              // snippet_info фронт тут не показывает, но если нужно:
              snippet_info: "Indexing not possible, please try again later",
            }
          : u
      )
    );
  } catch (err) {
    alert("Failed to run final index check: " + err.message);
  } finally {
    setFinalCheckLoading(false);
  }
};


  const runManualCheckOne = async (urlId) => {
    if (!window.confirm("Запустить разовую проверку для этой ссылки?")) return;

    try {
      setSingleCheckLoadingId(urlId);

      // сразу локально ставим In progress
      const nowIso = new Date().toISOString();
      setUrls((prev) =>
        prev.map((u) =>
          u.id === urlId
            ? { ...u, manual_check_status: "In progress", manual_check_date: nowIso }
            : u
        )
      );

      const token = localStorage.getItem("token");
      const res = await fetch(`/api/admin/manual-index/${urlId}/run`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let msg = "Ошибка запуска проверки";
        try {
          const errJson = await res.json();
          if (errJson?.detail) msg = errJson.detail;
        } catch {}
        throw new Error(msg);
      }
    } catch (err) {
      alert("Не удалось запустить разовую проверку: " + err.message);
    } finally {
      setSingleCheckLoadingId(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUrls.length === 0) return;
    if (!window.confirm("Удалить выбранные ссылки?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/admin/delete-links`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ link_ids: selectedUrls }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Ошибка при удалении");
      }

      setSelectedUrls([]);
      fetchUrls();
    } catch (err) {
      alert("Не удалось удалить ссылки: " + err.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Manual Index Check - IndexBooster</title>
      </Helmet>
      <Card>
<Card.Header className="d-flex justify-content-between align-items-center">
  <h3 className="card-title m-0">Manual Index Check</h3>
  {isAdmin && (
    <div className="d-flex gap-2">
      <Button
        variant="outline-primary"
        onClick={runManualCheckAll}
        disabled={runAllLoading}
      >
        {runAllLoading ? "Running manual check..." : "Manual index check (all)"}
      </Button>
      <Button
        variant="outline-danger"
        onClick={runFinalCheckSelected}
        disabled={finalCheckLoading || selectedUrls.length === 0}
      >
        {finalCheckLoading
          ? "Running final check..."
          : "Final index check (refund)"}
      </Button>
    </div>
  )}
</Card.Header>


        <Card.Body className="border-bottom py-3">
          <StatusFilterBar
            summary={summary}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
          />
          <div className="d-flex justify-content-start mt-3">
            <InputGroup style={{ maxWidth: 300 }}>
              <InputGroup.Text>
                <IconSearch size={18} />
              </InputGroup.Text>
<Form.Control
  placeholder="Search by URL, user or project"
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
/>
            </InputGroup>
          </div>
        </Card.Body>

        <div className="table-responsive">
          <Table className="table card-table table-vcenter text-nowrap datatable">
            <thead>
              <tr>
                <th className="w-1">
                  <Form.Check
                    type="checkbox"
                    checked={selectedUrls.length > 0 && selectedUrls.length === urls.length}
                    onChange={(e) =>
                      setSelectedUrls(e.target.checked ? urls.map((u) => u.id) : [])
                    }
                  />
                </th>
                {renderSortHeader("URL", "url")}
                {renderSortHeader("Indexable?", "indexing_status")}
                {renderSortHeader("Created", "creation_date")}
                {renderSortHeader("Indexing Status", "index_google_status")}
                {renderSortHeader("Manual check status", "manual_check_status")}
                {renderSortHeader("Manual check date", "manual_check_date")}
                  {renderSortHeader("Redirects", "googlebot_redirects_count")}
  {renderSortHeader("Indexes", "google_index_count")}
                <th>User</th>
                <th>Project</th>
              </tr>
            </thead>
            <tbody>
              {error && (
                <tr>
                  <td colSpan="11">
                    <Alert variant="danger" className="mb-0">
                      {error}
                    </Alert>
                  </td>
                </tr>
              )}
              {loading ? (
                <tr>
                  <td colSpan="11" className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      style={{ width: "3rem", height: "3rem" }}
                      role="status"
                    >
                      <span className="visually-hidden">Загрузка...</span>
                    </div>
                    <div className="mt-2 text-muted">Загрузка данных...</div>
                  </td>
                </tr>
              ) : (
                urls.map((url) => (
                  <tr key={url.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUrls.includes(url.id)}
                        onChange={(e) => {
                          setSelectedUrls((prev) =>
                            e.target.checked
                              ? [...prev, url.id]
                              : prev.filter((id) => id !== url.id)
                          );
                        }}
                      />
                    </td>

                                                            <td style={{ whiteSpace: "normal", wordBreak: "break-word", minWidth: "300px", maxWidth: "100%", flexGrow: 1 }}>{url.url}</td>

                    <td className="text-center">
                      {url.indexing_status?.startsWith("YES") ? (
                        <span className="text-success">{url.indexing_status}</span>
                      ) : url.indexing_status?.startsWith("NO") ? (
                        <span className="text-danger">{url.indexing_status}</span>
                      ) : (
                        <span className="text-warning">Pending...</span>
                      )}
                    </td>

                    <td className="text-center">
  <div>{new Date(url.creation_date).toLocaleDateString()}</div>
  <div>{new Date(url.creation_date).toLocaleTimeString()}</div>
</td>

                    {/* Indexing Status — как было */}
                    <td className="text-center" style={{ minWidth: "220px" }}>
                      {url.index_google_status === "Request in progress" ? (
                        <div className="alert alert-info py-1 px-2 mb-0 w-100">
                          Request in progress
                        </div>
                      ) : url.index_google_status === "Non indexable (refunded)" ? (
                        <div className="alert alert-danger py-1 px-2 mb-0 w-100">
                          Non indexable (refunded)
                        </div>
                      ) : url.index_google_status === "Non indexed (refunded)" ? (
                        <div className="alert alert-danger py-1 px-2 mb-0 w-100">
                          Non indexed (refunded)
                        </div>
                      ) : url.index_google_status === "Indexed" ? (
                        <div className="alert alert-success py-1 px-2 mb-0 w-100">
                          Indexed
                        </div>
                      ) : (
                        <span className="text-muted"></span>
                      )}
                    </td>

                    {/* Manual check status в том же стиле + IconReload */}
                    <td className="text-center" style={{ minWidth: "240px" }}>
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <div className="flex-grow-1">
                          {url.manual_check_status === "In progress" ? (
                            <div className="alert alert-warning py-1 px-2 mb-0 w-100">
                              In progress
                            </div>
                          ) : url.manual_check_status === "checked" ? (
                            <div className="alert alert-success py-1 px-2 mb-0 w-100">
                              Checked
                            </div>
                          ) : (
                            <div className="alert alert-secondary py-1 px-2 mb-0 w-100">
                              -
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="btn-icon d-inline-flex align-items-center justify-content-center"
                          disabled={
                            singleCheckLoadingId === url.id ||
                            url.index_google_status !== "Request in progress"
                          }
                          onClick={() => runManualCheckOne(url.id)}
                          title="Check index now"
                        >
                          <IconReload
                            size={18}
                            className={
                              singleCheckLoadingId === url.id ? "spin" : undefined
                            }
                          />
                        </Button>
                      </div>
                    </td>


<td className="text-center">
  {url.manual_check_date ? (
    <>
      <div>{new Date(url.manual_check_date).toLocaleDateString()}</div>
      <div>{new Date(url.manual_check_date).toLocaleTimeString()}</div>
    </>
  ) : (
    "-"
  )}
</td>
<td className="text-center">
  {typeof url.googlebot_redirects_count === "number"
    ? url.googlebot_redirects_count
    : 0}
</td>

<td className="text-center">
  {typeof url.google_index_count === "number"
    ? url.google_index_count
    : 0}
</td>
                    <td>
                      {url.user ? (
                        <Link
                          to={`/user/${url.user.id}/projects`}
                          className="text-primary"
                        >
                          {url.user.email}
                        </Link>
                      ) : (
                        ""
                      )}
                    </td>

                    <td>
                      {url.project ? (
                        <Link
                          to={`/project/${url.project.id}`}
                          className="text-primary"
                        >
                          {url.project.name}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <Card.Footer className="px-4">
          <div className="d-flex flex-column gap-2 w-100">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 w-100">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <Pagination className="m-0 flex-wrap">
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    «
                  </Pagination.Prev>
                  {getPaginationPages(currentPage, totalPages).map((page, i) =>
                    page === "..." ? (
                      <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                    ) : (
                      <Pagination.Item
                        key={`page-${page}`}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  )}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    »
                  </Pagination.Next>
                </Pagination>

                <div className="text-muted">
                  Showing <b>{(currentPage - 1) * urlsPerPage + 1}</b>–
                  <b>{Math.min(currentPage * urlsPerPage, totalCount)}</b> of{" "}
                  <b>{totalCount}</b> entries
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 ms-md-auto">
                <span className="text-muted">Show</span>
                <Form.Select
                  value={urlsPerPage}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setUrlsPerPage(Number(e.target.value));
                  }}
                  size="sm"
                >
                  {[10, 25, 50, 100, 200, 500].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Form.Select>
                <span className="text-muted">entries</span>
              </div>
            </div>

            <div className="d-flex justify-content-start gap-2">
              <Button
                variant="danger"
                disabled={selectedUrls.length === 0}
                onClick={handleDeleteSelected}
              >
                Delete Selected
              </Button>
            </div>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default ManualIndexTasks;
